/** @jsxImportSource @emotion/react */
import facepaint from 'facepaint'

import { ReactComponent as Logo } from '../../../assets/OptionLab_whiteText.svg'
import heroImage from '../../../assets/img/hero.jpg'

const breakpoints = [576, 768, 992, 1200]
const mediaQuery = facepaint(breakpoints.map(bp => `@media(min-width: ${bp}px)`))

function Hero() {
  return (
    <div css={style.heroContainer}>
      <div css={style.heroContent}>
        <Logo css={style.logo} title="OptionLab Logo" />
        <p css={style.tagline}>
          Decision support for individuals, teams, and enterprises — make more strategic decisions and unlock
          your organization's collective wisdom.
        </p>
      </div>
    </div>
  )
}

export function HomePage() {
  return (
    <div css={style.page}>
      <Hero />
      <div css={style.content}>
        <p css={style.lead}>
          OptionLab is a decision-support platform that helps teams and leaders weigh options, prioritize
          what matters, and make better strategic decisions. Existing customers can{' '}
          <a href="/signin">sign in</a> to continue.
        </p>
        <p css={style.body}>
          Interested in purchasing a license or seeing OptionLab in action? Email us at{' '}
          <a href="mailto:sales@optionlab.com">sales@optionlab.com</a> to request a demo or get started.
        </p>
      </div>
      <footer css={style.footer}>
        <div>
          &copy;{new Date().getFullYear()} <a href="https://optionlabinc.com">OptionLab Inc.</a>
          <br />
          5020 NE Martin Luther King Jr Blvd, Portland, OR 97211, United States
        </div>
        <div css={style.footerTrademark}>
          Microsoft and Microsoft Excel are trademarks of the Microsoft group of companies.
        </div>
      </footer>
    </div>
  )
}

const style = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    // Pure white — crisp, modern, and makes the dark hero pop. Cool near-neutral
    // tints read as "drab gray," so we avoid them here.
    background: '#ffffff',
  },
  heroContainer: mediaQuery({
    position: 'relative',
    backgroundImage: `url(${heroImage})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    minHeight: ['200px', '240px', '300px', '380px'],
    padding: ['24px 40px', '28px 52px', '32px 76px', '40px 112px'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000000',
      opacity: '0.75',
    },
  }),
  heroContent: {
    maxWidth: '800px',
    position: 'relative',
    color: '#ffffff',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  },
  logo: {
    // ~25% smaller than the SVG's intrinsic 600px width.
    width: '450px',
    maxWidth: '80%',
    height: 'auto',
    alignSelf: 'center',
  },
  tagline: {
    fontSize: '1.5em',
  },
  content: {
    flex: 1,
    // Centered reading column (~max-w-2xl). The single indentation level is the
    // column's side padding; the logo lives in the hero and isn't constrained by this.
    width: '100%',
    maxWidth: '720px',
    margin: '0 auto',
    boxSizing: 'border-box',
    padding: '2em 24px 2.5em',
  },
  lead: {
    fontSize: '1.6em',
    marginBottom: '1.25em',
  },
  body: {
    fontSize: '1.15em',
    marginBottom: '1em',
  },
  card: mediaQuery({
    textAlign: 'center',
    background: '#ffffff',
    paddingTop: '20px 12px 6px',
    boxShadow: '0 0 12px 1px rgba(0,0,0,0.2)',
    height: '100%',
    ul: {
      textAlign: 'left',
    },
    p: {
      textAlign: 'left',
    },
  }),
  cardPara: {
    fontSize: '1.2em',
  },
  cardSpan: {
    sm: 24,
    md: 12,
    lg: 8,
  },
  icon: {
    fontSize: '40px',
  },
  pricing: {
    padding: '30px',
  },
  footer: {
    width: '100%',
    fontSize: '0.8em',
    marginTop: '12px',
    padding: '24px',
  },
  footerTrademark: {
    marginTop: '1.5em',
  },
}
